/**
 * Month-by-month lost sales across a bulk run, built only from the monthly
 * breakdown each scope already returns (`include_breakdown: "monthly"`), so
 * the chart costs no extra request or server work.
 *
 * Series identity is fixed by lost UNITS rank (top N + "Other") and does not
 * change with the metric shown, so a SKU keeps its color when toggling to
 * revenue.
 */

export const TIMELINE_TOP_SERIES = 5;
export const OTHER_SERIES_KEY = "other";

const monthKeysBetween = (startDate, endDate) => {
  if (!startDate || !endDate || startDate > endDate) return [];
  const keys = [];
  let [year, month] = startDate.slice(0, 7).split("-").map(Number);
  const [endYear, endMonth] = endDate.slice(0, 7).split("-").map(Number);
  while (year < endYear || (year === endYear && month <= endMonth)) {
    keys.push(`${year}-${String(month).padStart(2, "0")}`);
    month += 1;
    if (month > 12) {
      month = 1;
      year += 1;
    }
  }
  return keys;
};

const lastDayOfMonth = (monthKey) => {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(Date.UTC(year, month, 0)).getUTCDate();
};

export const formatTimelineMonth = (monthKey) => {
  const [year, month] = monthKey.split("-").map(Number);
  return new Date(Date.UTC(year, month - 1, 1)).toLocaleString("en-US", {
    month: "short",
    year: "2-digit",
    timeZone: "UTC",
  });
};

const scopeLabel = (row, productLabelById) => {
  const scope = row.scope || {};
  if (scope.analysis_level !== "product" && scope.sku) return scope.sku;
  return (
    scope.product_name ||
    productLabelById?.get?.(String(scope.product)) ||
    String(scope.product || "—")
  );
};

/** Single currency shared by every priced scope, or null (none / mixed). */
const revenueAvailability = (rows) => {
  const currencies = new Set();
  let partial = false;
  rows.forEach((row) => {
    const estimate = row.revenue_estimate;
    if (!estimate || estimate.estimated_lost_revenue == null || !estimate.currency) {
      if ((Number(row.metrics?.estimated_lost_units) || 0) > 0) partial = true;
      return;
    }
    if (!estimate.complete) partial = true;
    currencies.add(estimate.currency);
  });
  if (currencies.size !== 1) {
    return { available: false, currency: null, partial, mixed: currencies.size > 1 };
  }
  return { available: true, currency: [...currencies][0], partial, mixed: false };
};

/**
 * @returns {{
 *   series: {key: string, label: string}[],
 *   months: {month, label, partial, hasData, units: object, revenue: object,
 *            totalUnits: number, totalRevenue: number}[],
 *   revenue: {available, currency, partial, mixed},
 *   analyzedCount: number,
 * } | null} null when no scope has a monthly breakdown.
 */
export const buildBulkLostSalesTimeline = ({
  rows = [],
  startDate,
  endDate,
  productLabelById,
  topN = TIMELINE_TOP_SERIES,
}) => {
  const analyzed = (rows || []).filter(
    (row) => row?.status === "complete" && Array.isArray(row.breakdown?.monthly),
  );
  if (!analyzed.length) return null;

  const monthKeys = monthKeysBetween(startDate, endDate);
  if (!monthKeys.length) return null;

  const ranked = [...analyzed].sort(
    (left, right) =>
      (Number(right.metrics?.estimated_lost_units) || 0) -
      (Number(left.metrics?.estimated_lost_units) || 0),
  );
  // Only scopes that lost something earn their own series; folding a lone
  // remainder into "Other" would hide a named SKU behind a vaguer label.
  const contributors = ranked.filter(
    (row) => (Number(row.metrics?.estimated_lost_units) || 0) > 0,
  );
  const named =
    contributors.length <= topN + 1 ? contributors : contributors.slice(0, topN);
  const series = named.map((row, index) => ({
    key: `s${index}`,
    label: scopeLabel(row, productLabelById),
  }));
  const seriesKeyByRow = new Map(named.map((row, index) => [row, `s${index}`]));
  const hasOther = contributors.length > named.length;
  if (hasOther) {
    series.push({
      key: OTHER_SERIES_KEY,
      label: `Other (${contributors.length - named.length})`,
    });
  }

  const months = monthKeys.map((month, index) => {
    const startsMidMonth = index === 0 && Number(startDate.slice(8, 10)) !== 1;
    const endsMidMonth =
      index === monthKeys.length - 1 &&
      Number(endDate.slice(8, 10)) !== lastDayOfMonth(month);
    return {
      month,
      label: formatTimelineMonth(month),
      partial: startsMidMonth || endsMidMonth,
      hasData: false,
      units: Object.fromEntries(series.map((item) => [item.key, 0])),
      revenue: Object.fromEntries(series.map((item) => [item.key, 0])),
      totalUnits: 0,
      totalRevenue: 0,
    };
  });
  const monthIndex = new Map(months.map((entry, index) => [entry.month, index]));

  analyzed.forEach((row) => {
    const key = seriesKeyByRow.get(row) || (hasOther ? OTHER_SERIES_KEY : null);
    row.breakdown.monthly.forEach((monthRow) => {
      const index = monthIndex.get(String(monthRow?.month || ""));
      if (index == null) return;
      const entry = months[index];
      // A month listed in the breakdown had analyzable days, even at 0 lost.
      entry.hasData = true;
      if (!key) return;
      const units = Number(monthRow.estimated_lost_units) || 0;
      const revenue = Number(monthRow.estimated_lost_revenue) || 0;
      entry.units[key] += units;
      entry.revenue[key] += revenue;
      entry.totalUnits += units;
      entry.totalRevenue += revenue;
    });
  });

  return {
    series,
    months,
    revenue: revenueAvailability(analyzed),
    analyzedCount: analyzed.length,
  };
};

/** Month with the largest total for the metric (null when everything is 0). */
export const peakTimelineMonth = (timeline, metric = "units") => {
  if (!timeline) return null;
  const field = metric === "revenue" ? "totalRevenue" : "totalUnits";
  const total = timeline.months.reduce((sum, entry) => sum + entry[field], 0);
  if (!(total > 0)) return null;
  const peak = timeline.months.reduce((best, entry) =>
    entry[field] > best[field] ? entry : best,
  );
  return { ...peak, value: peak[field], share: peak[field] / total };
};
